/**
 * GlobalToastStack — proaktywne alerty tła dla operatora.
 * Ported from school-transport-platform 2026-06-07.
 *
 * Co 30 s odpytuje 3 endpointy (zdarzenia, powiadomienia, zgłoszenia)
 * i pokazuje toast dla KAŻDEGO nowego elementu (diff po ID vs localStorage).
 * Uzupełnia sonner (toasty z akcji użytkownika) o żywe alerty systemowe.
 *
 * Różnice vs źródło: bez MessagesModule (brak w ZUR-OPS), bez WebSocket,
 * react-router zamiast next/link, UI po polsku, klucze localStorage `zur-`.
 *
 * Reguły czasu życia:
 *   • powiadomienie / zgłoszenie → auto-znika po 8-10 s
 *   • zdarzenie LOW/MED          → 12 s, cichy sygnał
 *   • zdarzenie HIGH             → 30 s, sygnał
 *   • zdarzenie CRITICAL         → zostaje aż operator zamknie, podwójny sygnał
 *
 * Pierwsze odpytanie tylko zasiewa "widziane" ID bez pokazywania toastów
 * (żeby po otwarciu panelu nie wysypać dziesiątek starych elementów).
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Bug, AlertTriangle, X, Flag, FileText, Wine } from 'lucide-react';
import { api } from '@/shared/lib/api';
import { useAuthStore } from '@store/auth.store';

type ToastKind = 'incident' | 'notification' | 'feedback' | 'form';
type Severity  = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

interface BgToast {
  key:       string;
  kind:      ToastKind;
  severity?: Severity;
  title:     string;
  body?:     string;
  href:      string;
  ttlMs:     number;   // 0 = sticky
}

const SEEN_KEY = (k: string) => `zur-toast-seen-${k}`;

function loadSeen(kind: string): Set<string> {
  try { const raw = localStorage.getItem(SEEN_KEY(kind)); return new Set(raw ? JSON.parse(raw) as string[] : []); }
  catch { return new Set(); }
}
function saveSeen(kind: string, ids: Set<string>) {
  try {
    const arr = Array.from(ids);
    localStorage.setItem(SEEN_KEY(kind), JSON.stringify(arr.length > 500 ? arr.slice(arr.length - 500) : arr));
  } catch {}
}

/* Krótki sygnał Web Audio — bez pliku dźwiękowego. */
function beep(freq: number, durationMs: number, gain = 0.05) {
  try {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine'; osc.frequency.value = freq; g.gain.value = gain;
    osc.connect(g); g.connect(ctx.destination); osc.start();
    setTimeout(() => { osc.stop(); ctx.close(); }, durationMs);
  } catch {}
}

export function GlobalToastStack() {
  const [toasts, setToasts] = useState<BgToast[]>([]);
  const seedDoneRef = useRef<Set<string>>(new Set());
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const dismiss = (key: string) => setToasts(prev => prev.filter(t => t.key !== key));

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    async function pullKind(
      kind: ToastKind,
      fetcher: () => Promise<any>,
      extract: (data: any) => Array<{ id: string; title: string; body?: string; href: string; severity?: Severity; kind?: ToastKind }>,
    ) {
      let res: any = null;
      try { res = await fetcher(); } catch { return; }
      if (cancelled || res == null) return;
      const items = extract(res);

      // Mark this kind as seeded on the FIRST successful poll — even when the
      // list is empty — so the first item that arrives later is treated as NEW
      // (and toasts) instead of being silently swallowed as a first-load seed.
      // Bug fixed 2026-06-24: the empty-list early return used to run BEFORE the
      // seed marking, so the first form/message (queues are usually empty at
      // mount) never popped a toast even though it reached the bell.
      const seen = loadSeen(kind);
      const isFirstLoad = !seedDoneRef.current.has(kind);
      seedDoneRef.current.add(kind);

      if (items.length === 0) return;

      const fresh = items.filter(i => !seen.has(i.id));
      if (fresh.length === 0) return;
      fresh.forEach(i => seen.add(i.id));
      saveSeen(kind, seen);
      if (isFirstLoad) return;   // tylko zasiew, bez toastów

      // A new background alert means the matching admin list is now stale. The
      // bell/toast streams poll independently of the page queries, so without
      // this the operator sees the toast but the form/incident/feedback only
      // shows up after a manual refresh. Invalidate the relevant lists so they
      // refresh together with the toast (no socket in this app — see header).
      const freshKinds = new Set(fresh.map(i => i.kind ?? kind));
      if (freshKinds.has('form'))     qc.invalidateQueries({ queryKey: ['form-submissions'] });
      if (freshKinds.has('incident')) qc.invalidateQueries({ queryKey: ['incidents'] });
      if (freshKinds.has('feedback')) qc.invalidateQueries({ queryKey: ['feedback'] });
      qc.invalidateQueries({ queryKey: ['sidebar', 'unread-notifications'] });

      const burst = fresh.slice(0, 3).map(i => {
        // An item may override the poll's kind (e.g. a FORM_SUBMITTED notification
        // is rendered as a 'form' toast). Non-incident alerts live long enough not
        // to be missed while the operator is looking at the bell; critical incidents
        // stay sticky.
        const itemKind = i.kind ?? kind;
        const sev = i.severity;
        const ttlMs =
          itemKind === 'incident' && sev === 'CRITICAL' ? 0 :
          itemKind === 'incident' && sev === 'HIGH'     ? 30_000 :
          itemKind === 'incident'                       ? 15_000 :
                                                          25_000; // form / message / notification / feedback
        return { key: `${itemKind}-${i.id}`, kind: itemKind, severity: sev, title: i.title, body: i.body, href: i.href, ttlMs } as BgToast;
      });

      setToasts(prev => [...prev, ...burst].slice(-5));

      const crit = burst.find(b => b.severity === 'CRITICAL');
      const high = burst.find(b => b.severity === 'HIGH');
      const lowMed = burst.find(b => b.kind === 'incident' && b.severity !== 'CRITICAL' && b.severity !== 'HIGH');
      if (crit)       { beep(880, 200, 0.10); setTimeout(() => beep(720, 200, 0.10), 220); }
      else if (high)  { beep(660, 200, 0.07); }
      else if (lowMed){ beep(520, 150, 0.04); }
      // Soft chime for forms / messages / feedback so a new item is noticed.
      else if (burst.length) { beep(600, 130, 0.05); }

      burst.forEach(b => { if (b.ttlMs > 0) window.setTimeout(() => dismiss(b.key), b.ttlMs); });
    }

    async function pollOnce() {
      if (document.visibilityState !== 'visible') return;
      await Promise.allSettled([
        pullKind('incident',
          () => api.get('/incidents', { limit: 20 }),
          (raw) => extractIncidents(raw)),
        // Forms + messages are toasted straight from the notification stream (the
        // same data as the bell), so "in the bell ⟺ a toast pops". Each item picks
        // its own kind/href in extractNotifications. Incidents keep their dedicated
        // poll (severity-based styling/sound) and stay excluded from this stream.
        pullKind('notification',
          () => api.get('/notifications', { unreadOnly: 'true' }),
          (raw) => extractNotifications(raw)),
        pullKind('feedback',
          () => api.get('/feedback', { limit: 20 }).catch(() => null),
          (raw) => extractFeedback(raw)),
      ]);
    }

    pollOnce();
    interval = setInterval(pollOnce, 15_000);
    const onVisible = () => { if (document.visibilityState === 'visible') pollOnce(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-16 right-4 z-[10000] flex flex-col gap-2 pointer-events-none" aria-live="polite">
      <AnimatePresence>
        {toasts.map(t => {
          const accent = colorFor(t);
          return (
            <motion.div key={t.key}
              initial={{ opacity: 0, x: 60, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 60, scale: 0.95 }}
              transition={{ duration: 0.22 }}
              className="pointer-events-auto w-[320px] rounded-xl bg-card shadow-xl border border-border overflow-hidden"
              style={{ borderLeft: `4px solid ${accent}` }}>
              <button onClick={() => { navigate(t.href); dismiss(t.key); }}
                className="block w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors">
                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${accent}18`, color: accent }}>
                    {iconFor(t)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-[10px] font-extrabold uppercase tracking-wider" style={{ color: accent }}>{labelFor(t)}</p>
                      {t.severity && (
                        <span className="text-[8.5px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: `${accent}22`, color: accent }}>
                          {sevLabel(t.severity)}
                        </span>
                      )}
                    </div>
                    <p className="text-[12.5px] font-black text-foreground truncate mt-0.5">{t.title}</p>
                    {t.body && <p className="text-[11.5px] text-muted-foreground line-clamp-2 mt-0.5">{t.body}</p>}
                  </div>
                  <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismiss(t.key); }}
                    className="p-1 rounded hover:bg-muted text-muted-foreground flex-shrink-0 cursor-pointer" title="Zamknij">
                    <X className="w-3 h-3" />
                  </span>
                </div>
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────
function colorFor(t: BgToast): string {
  if (t.kind === 'incident' && t.severity === 'CRITICAL') return '#7c3aed';
  if (t.kind === 'incident' && t.severity === 'HIGH')     return '#dc2626';
  if (t.kind === 'incident')                              return '#f59e0b';
  if (t.kind === 'form')                                  return '#3b82f6';
  if (t.kind === 'notification' && t.severity === 'CRITICAL') return '#e11d48'; // B-653: alarm trzeźwości
  if (t.kind === 'notification')                          return '#eab308';
  return '#0d9488'; // feedback
}
function labelFor(t: BgToast): string {
  if (t.kind === 'incident')     return 'Zdarzenie';
  if (t.kind === 'form')         return 'Formularz';
  if (t.kind === 'notification') return 'Powiadomienie';
  return 'Zgłoszenie';
}
function sevLabel(s: Severity): string {
  return s === 'LOW' ? 'Niski' : s === 'MEDIUM' ? 'Średni' : s === 'HIGH' ? 'Wysoki' : 'Krytyczny';
}
function iconFor(t: BgToast) {
  const cls = 'w-4 h-4';
  if (t.kind === 'incident' && t.severity === 'CRITICAL') return <AlertTriangle className={cls} />;
  if (t.kind === 'incident')     return <Flag className={cls} />;
  if (t.kind === 'form')         return <FileText className={cls} />;
  if (t.kind === 'notification' && t.severity === 'CRITICAL') return <Wine className={cls} />; // B-653
  if (t.kind === 'notification') return <Bell className={cls} />;
  return <Bug className={cls} />;
}

// ── extractory (api.get rozpakowuje envelope → raw to data) ───────────────────
function asList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}
function extractIncidents(raw: any) {
  return asList(raw)
    .filter(i => i?.id && (i.status === 'OPEN' || i.status === 'IN_PROGRESS'))
    .map(i => ({
      id: i.id,
      severity: (i.severity ?? 'MEDIUM') as Severity,
      title: i.title ?? 'Nowe zdarzenie',
      body: (i.description ?? '').slice(0, 110),
      href: '/incidents',
    }));
}
// Incidents keep their own dedicated poll (severity styling/sound), so their
// notification is skipped here to avoid a double toast. Forms + messages are
// toasted from this stream.
const NOTIF_HANDLED_ELSEWHERE = new Set(['INCIDENT_CREATED']);
function extractNotifications(raw: any) {
  return asList(raw)
    .filter(n => n?.id && !n.readAt && !n.isRead && !NOTIF_HANDLED_ELSEWHERE.has(n.type))
    .map(n => {
      const type: string = n.type ?? '';
      // A FORM_* notification renders as a blue "Formularz" toast linking to the
      // forms queue; a MESSAGE links to the conversation list; everything else is
      // a generic notification.
      const kind: ToastKind = type.startsWith('FORM') ? 'form' : 'notification';
      /* B-653: alarm kontroli trzeźwości — krytyczny (podwójny sygnał, czerwony toast) i prosto do dokumentu.
         Samo przyjęcie alarmu dzieje się w oknie `SobrietyAlertPopup`; toast tylko zwraca uwagę. */
      const trzezwosc = n.entityType === 'SobrietyAlert';
      const dokument: string | undefined = typeof n.metadata?.submissionId === 'string' ? n.metadata.submissionId : undefined;
      // Deep-link straight to the entity when we have its id (there is no
      // `/forms` index route — linking there shows a blank page). FormSubmission
      // → /forms/:id; otherwise fall back to the list.
      const href =
        trzezwosc ? (dokument ? `/forms/${dokument}` : '/notifications') :
        type.startsWith('FORM') ? (n.entityId ? `/forms/${n.entityId}` : '/forms/my') :
        type === 'MESSAGE'      ? '/messages' :
        // B-161: a new employee request opens the planner with its panel already
        // slid out — landing on /notifications would just be one more click.
        n.entityType === 'ScheduleRequest' ? '/planner?wnioski=1' :
                                  '/notifications';
      return {
        id: n.id,
        title: n.title ?? 'Nowe powiadomienie',
        body: (n.body ?? n.message ?? '').slice(0, 110),
        href,
        kind,
        ...(trzezwosc ? { severity: 'CRITICAL' as Severity } : {}),
      };
    });
}
function extractFeedback(raw: any) {
  return asList(raw)
    .filter(f => f?.id && (f.status === 'NEW' || f.status === 'IN_PROGRESS' || f.status === 'IN_REVIEW'))
    .slice(0, 5)
    .map(f => ({
      id: f.id,
      title: f.title ?? `Zgłoszenie ${f.ticketNumber ?? ''}`.trim(),
      body: (f.description ?? '').slice(0, 110),
      href: '/feedback',
    }));
}
